import { serverRouteConfig } from "@server/serverRouteConfig"
import { createFileRoute } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"

const BASE_URL = "https://playbrain.com"

type SlugItem = { language: "ja" | "en"; slug?: { current?: string } }

function serverFnPayload<T>(value: T): never {
  return value as never
}

const getSitemapSlugData = createServerFn({ method: "GET" }).handler(async () => {
  const { getPublicDocumentsByType } = await import("@data/publicPageData.server")
  const [newsData, careersData] = await Promise.all([
    getPublicDocumentsByType<SlugItem>({ type: "news" }),
    getPublicDocumentsByType<SlugItem>({ type: "careers" }),
  ])
  return serverFnPayload({ newsData, careersData })
})

function localizedUrl(locale: "ja" | "en", page: string) {
  return page ? `${BASE_URL}/${locale}/${page}` : `${BASE_URL}/${locale}`
}

function xmlAttr(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
}

function xmlUrl(value: string) {
  return xmlAttr(value)
}

function groupedSlugs(slugs: Set<string>) {
  const grouped = new Map<string, Set<string>>()
  slugs.forEach((entry) => {
    const [lang, name] = entry.split("/")
    if (!grouped.has(name)) grouped.set(name, new Set())
    grouped.get(name)?.add(lang)
  })
  return grouped
}

function generateXmlOutput(grouped: Map<string, Set<string>>, category: string) {
  let xmlOutput = ""
  grouped.forEach((langs, name) => {
    const baseUrlJa = xmlUrl(`${BASE_URL}/ja/${category}/${encodeURIComponent(name)}`)
    const baseUrlEn = xmlUrl(`${BASE_URL}/en/${category}/${encodeURIComponent(name)}`)
    if (langs.has("en") && langs.has("ja")) {
      xmlOutput += `<url><loc>${baseUrlJa}</loc><xhtml:link rel="alternate" hreflang="en" href="${baseUrlEn}"/><xhtml:link rel="alternate" hreflang="ja" href="${baseUrlJa}"/></url>`
    } else if (langs.has("en")) {
      xmlOutput += `<url><loc>${baseUrlEn}</loc><xhtml:link rel="alternate" hreflang="en" href="${baseUrlEn}"/></url>`
    } else if (langs.has("ja")) {
      xmlOutput += `<url><loc>${baseUrlJa}</loc><xhtml:link rel="alternate" hreflang="ja" href="${baseUrlJa}"/></url>`
    }
  })
  return xmlOutput
}

export const Route = createFileRoute("/sitemap.xml")(
  serverRouteConfig({
    server: {
      handlers: {
        GET: async () => {
          const { newsData, careersData } = (await getSitemapSlugData()) as {
            careersData: SlugItem[]
            newsData: SlugItem[]
          }
          const pages = ["", "careers", "contact", "news", "calendar", "company", "work"].flatMap((page) => {
            const jaUrl = localizedUrl("ja", page)
            const enUrl = localizedUrl("en", page)
            return [
              `<url><loc>${xmlUrl(jaUrl)}</loc><xhtml:link rel="alternate" hreflang="en" href="${xmlUrl(enUrl)}"/><xhtml:link rel="alternate" hreflang="ja" href="${xmlUrl(jaUrl)}"/></url>`,
              `<url><loc>${xmlUrl(enUrl)}</loc><xhtml:link rel="alternate" hreflang="en" href="${xmlUrl(enUrl)}"/><xhtml:link rel="alternate" hreflang="ja" href="${xmlUrl(jaUrl)}"/></url>`,
            ]
          })
          const newsSlugs = new Set(
            newsData
              .filter((item) => item.slug?.current)
              .map((item) => `${item.language}/${item.slug?.current}`)
          )
          const careerSlugs = new Set(
            careersData
              .filter((item) => item.slug?.current)
              .map((item) => `${item.language}/${item.slug?.current}`)
          )
          const result = `<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">${pages.join("")}${generateXmlOutput(groupedSlugs(newsSlugs), "news")}${generateXmlOutput(groupedSlugs(careerSlugs), "careers")}${generateXmlOutput(groupedSlugs(careerSlugs), "contact")}</urlset>`
          return new Response(result, { headers: { "Content-Type": "application/xml" } })
        },
      },
    },
  })
)
